import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/Card';
import { Button } from '../components/Button';
import { ArrowLeft, BarChart2, AlertTriangle, MapPin, TrendingUp, ShieldAlert, FileText } from 'lucide-react';
import { useStore, isOfficialProduct } from '../data/store';
import { UrgencyBadge } from '../components/UrgencyBadge';
import { StatusBadge } from '../components/StatusBadge';
export default function IntelligenceSummary() {
  const navigate = useNavigate();
  const reports = useStore(s => s.reports);
  const officialReports = reports.filter(isOfficialProduct);
  const criticalReports = officialReports.filter(r => r.urgency === 'Kritis' || r.urgency === 'KRITIS');
  const escalatedReports = officialReports.filter(r => r.escalatedNational);
  // Calculate priority regions
  const regionCounts = reports.reduce((acc, r) => {
    acc[r.region] = (acc[r.region] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  const priorityRegions = Object.entries(regionCounts).sort((a, b) => b[1] - a[1]).slice(0, 5);
  // Calculate top categories
  const categoryCounts = reports.reduce((acc, r) => {
    acc[r.category] = (acc[r.category] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  const topCategories = Object.entries(categoryCounts).sort((a, b) => b[1] - a[1]).slice(0, 3);
  return <div className="space-y-6 animate-in fade-in duration-500 max-w-6xl mx-auto pb-12" data-id="element-1258">
      <div className="flex items-center gap-4 border-b border-border/50 pb-4" data-id="element-1259">
        <Button variant="ghost" size="icon" onClick={() => navigate('/reports')} className="hover:bg-primary/20 hover:text-primary" data-id="element-1260">
          <ArrowLeft className="w-5 h-5" data-id="element-1261" />
        </Button>
        <div data-id="element-1262">
          <h1 className="text-2xl font-heading font-bold tracking-tight text-foreground flex items-center gap-3" data-id="element-1263">
            EXECUTIVE INTELLIGENCE SUMMARY
          </h1>
          <p className="text-muted-foreground font-mono text-xs mt-1 uppercase tracking-widest" data-id="element-1264">
            DENS CAKRA // Ringkasan Situasi Nasional
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1265">
        <Card className="glass-panel border-border/50" data-id="element-1266">
          <CardHeader className="border-b border-border/50 bg-card/50" data-id="element-1267">
            <CardTitle className="text-sm font-mono uppercase tracking-widest flex items-center gap-2" data-id="element-1268">
              <TrendingUp className="w-4 h-4 text-primary" data-id="element-1269" /> Top Issues
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-4" data-id="element-1270">
            {topCategories.map(([cat, count], i) => <div key={cat} className="flex justify-between items-center" data-id="element-1271">
                <div className="flex items-center gap-2" data-id="element-1272">
                  <span className="text-muted-foreground font-mono w-4" data-id="element-1273">
                    {i + 1}.
                  </span>
                  <span className="font-bold text-sm" data-id="element-1274">{cat}</span>
                </div>
                <span className="text-xs font-mono text-primary" data-id="element-1275">
                  {count} Laporan
                </span>
              </div>)}
          </CardContent>
        </Card>

        <Card className="glass-panel border-border/50" data-id="element-1276">
          <CardHeader className="border-b border-border/50 bg-card/50" data-id="element-1277">
            <CardTitle className="text-sm font-mono uppercase tracking-widest flex items-center gap-2" data-id="element-1278">
              <MapPin className="w-4 h-4 text-primary" data-id="element-1279" /> Priority Regions
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-4" data-id="element-1280">
            {priorityRegions.map(([reg, count], i) => <div key={reg} className="flex justify-between items-center" data-id="element-1281">
                <div className="flex items-center gap-2" data-id="element-1282">
                  <span className="text-muted-foreground font-mono w-4" data-id="element-1283">
                    {i + 1}.
                  </span>
                  <span className="font-bold text-sm" data-id="element-1284">{reg}</span>
                </div>
                <span className="text-xs font-mono text-primary" data-id="element-1285">
                  {count} Insiden
                </span>
              </div>)}
          </CardContent>
        </Card>

        <Card className="glass-panel border-border/50" data-id="element-1286">
          <CardHeader className="border-b border-border/50 bg-card/50" data-id="element-1287">
            <CardTitle className="text-sm font-mono uppercase tracking-widest flex items-center gap-2" data-id="element-1288">
              <BarChart2 className="w-4 h-4 text-primary" data-id="element-1289" /> Emerging Trends
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-3" data-id="element-1290">
            <div className="p-3 bg-black/30 rounded border border-border/30 text-xs text-foreground/80 leading-relaxed" data-id="element-1291">
              Peningkatan aktivitas siber di wilayah Jawa Barat dan DKI Jakarta
              dalam 48 jam terakhir.
            </div>
            <div className="p-3 bg-black/30 rounded border border-border/30 text-xs text-foreground/80 leading-relaxed" data-id="element-1292">
              Konsentrasi massa terpantau meningkat di beberapa titik objek
              vital nasional.
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1293">
        <Card className="glass-panel border-destructive/30" data-id="element-1294">
          <CardHeader className="border-b border-destructive/30 bg-destructive/10" data-id="element-1295">
            <CardTitle className="text-sm font-mono uppercase tracking-widest flex items-center gap-2 text-destructive" data-id="element-1296">
              <AlertTriangle className="w-4 h-4" data-id="element-1297" /> Critical Reports
            </CardTitle>
          </CardHeader>

          <CardContent className="p-0" data-id="element-1298">
            <div className="divide-y divide-border/30" data-id="element-1299">
              {criticalReports.length === 0 ? <>
                  <div className="p-6 text-center text-xs font-mono text-muted-foreground" data-id="element-1300">
                    Tidak ada laporan kritis.
                  </div>
                </> : criticalReports.map(r => <div key={r.id} className="p-4 hover:bg-white/5 cursor-pointer transition-colors flex justify-between items-center" onClick={() => navigate(`/reports/${r.id}`)} data-id="element-1301">
                    <div data-id="element-1302">
                      <div className="text-xs font-mono text-primary mb-1" data-id="element-1303">
                        {r.id}
                      </div>
                      <div className="text-sm font-medium" data-id="element-1304">{r.title}</div>
                      <div className="text-[10px] text-muted-foreground mt-1" data-id="element-1305">
                        {r.region} • {r.category}
                      </div>
                    </div>
                    <UrgencyBadge urgency={r.urgency as any} data-id="element-1306" />
                  </div>)}
            </div>
          </CardContent>
        </Card>
        <Card className="glass-panel border-border/50" data-id="element-1307">
          <CardHeader className="border-b border-border/50 bg-card/50" data-id="element-1308">
            <CardTitle className="text-sm font-mono uppercase tracking-widest flex items-center gap-2" data-id="element-1309">
              <ShieldAlert className="w-4 h-4 text-primary" data-id="element-1310" /> Escalated &amp;
              Analyzed
            </CardTitle>
          </CardHeader>

          <CardContent className="p-0" data-id="element-1311">
            <div className="divide-y divide-border/30" data-id="element-1312">
              {escalatedReports.length === 0 ? <>
                  <div className="p-6 text-center text-xs font-mono text-muted-foreground" data-id="element-1313">
                    Tidak ada laporan yang dieskalasi.
                  </div>
                </> : escalatedReports.map(r => <div key={r.id} className="p-4 hover:bg-white/5 cursor-pointer transition-colors flex justify-between items-center" onClick={() => navigate(`/reports/${r.id}`)} data-id="element-1314">
                    <div data-id="element-1315">
                      <div className="text-xs font-mono text-primary mb-1" data-id="element-1316">
                        {r.id}
                      </div>
                      <div className="text-sm font-medium" data-id="element-1317">{r.title}</div>
                      <div className="text-[10px] text-muted-foreground mt-1" data-id="element-1318">
                        {r.region}
                      </div>
                    </div>
                    <StatusBadge status={r.status} data-id="element-1319" />
                  </div>)}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>;
}